import { useEffect, useState, useMemo } from "react";
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { DailyScheduleRevenueDto } from "../../../../api-client";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";


const useMonthlyRevenue = (date?: Date, locationIds?: string[]) => {
    const BIStore = useStore(BIContext); 
    const { getMonthlyRevenue } = BIStore; 

    // Debounce date changes
    const debounceDate = useDebounce(date, 300);

    // Memoize locationIds to avoid unnecessary re-renders
    const memoizedLocationIds = useMemo(() => locationIds, [locationIds?.join(',')]);

    // Store all fetched stats (for "all locations")
    const [allMonthlyRevenue, setAllMonthlyRevenue] = useState<DailyScheduleRevenueDto[]>([]);
    // Store filtered stats per selected locations
    const [monthlyRevenue, setMonthlyRevenue] = useState<DailyScheduleRevenueDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // console.log("useMonthlyRevenue: Fetching data for", debounceDate?.toISOString());
        const fetchData = async () => {
            try {
                setIsLoading(true);

                let response;
                if (debounceDate) {
                    response = await getMonthlyRevenue(debounceDate);
                    // console.log("Revenue backend responded with:", response);
                } else {
                    response = await getMonthlyRevenue(undefined);
                }

                if (response) {
                    setAllMonthlyRevenue(response);
                    
                    // Apply location filter immediately when setting data
                    if (memoizedLocationIds && memoizedLocationIds.length > 0) {
                        setMonthlyRevenue(response.filter(p => memoizedLocationIds.includes(p.locationId)));
                    } else {
                        setMonthlyRevenue(response);
                    }
                } else {
                    setAllMonthlyRevenue([]);
                    setMonthlyRevenue([]);
                }
            } catch (error) {
                // console.log("Error fetching daily revenue: ", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [debounceDate, getMonthlyRevenue]);

    // Filter locally whenever locations change (but not when data is initially loaded)
    useEffect(() => {
        if (!isLoading && allMonthlyRevenue.length > 0) {
            if (memoizedLocationIds && memoizedLocationIds.length > 0) {
                setMonthlyRevenue(allMonthlyRevenue.filter(p => memoizedLocationIds.includes(p.locationId)));
            } else {
                // No location selected → show all
                setMonthlyRevenue(allMonthlyRevenue);
            }
        }
    }, [memoizedLocationIds, allMonthlyRevenue, isLoading]);

    const availableLocations = useMemo(() => {
        const locMap = new Map<string, string>();
        allMonthlyRevenue.forEach((p) => {
          if (p.locationId) locMap.set(p.locationId, p.locationId);
        });
        return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
      }, [allMonthlyRevenue]);

    return { monthlyRevenue, isLoading, availableLocations };
}

export default useMonthlyRevenue;